import React, { FC, useCallback, useEffect, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import { Visibility, Download } from '@mui/icons-material';
import { EBalanceType, ETransactionType, ITransaction, TransactionTypeTranslations } from 'common/transactions.interface';
import { formatDate } from 'utils/date';
import { formatNumberToBRL } from 'utils/formatters';
import { useBankingVisibility } from '../../../contexts/BankingVisibilityContext';
import ProofOfPaymentModal from '../modals/ProofOfPaymentModal';
import DescriptionModal from '../../../components/DescriptionModal';

export const TABLE_TRANSACTION_TYPE_TRANSLATIONS = {
  [ETransactionType.PIX_PAYMENT_IN]: 'PIX recebido',
  [ETransactionType.MANUAL_PIX_OUT]: 'PIX enviado',
  [ETransactionType.PIX_REVERSAL_IN]: 'PIX estornado',
  [ETransactionType.SPB_TRANSFER_IN]: 'TED recebida',
  [ETransactionType.BANK_SLIP]: 'Boleto/PIX pago',
  [ETransactionType.BANK_SLIP_IN]: 'Boleto recebido',
  [ETransactionType.BETWEEN_ACCOUNTS]: 'TEF',
};

interface TransactionTableProps {
  transactions: ITransaction[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}

export const TransactionTable: FC<TransactionTableProps> = ({ transactions, loading, hasMore, onLoadMore }) => {
  const observerTarget = useRef<HTMLDivElement | null>(null);
  const { showValues } = useBankingVisibility();

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;

      if (entry.isIntersecting && hasMore && !loading) {
        onLoadMore();
      }
    },
    [hasMore, loading, onLoadMore]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '20px',
      threshold: 0.1,
    });

    const target = observerTarget.current;
    if (target) observer.observe(target);

    return () => {
      if (target) observer.unobserve(target);
    };
  }, [handleObserver]);

  const [showProofOfPaymentModal, setShowProofOfPaymentModal] = React.useState(false);
  const [showDescriptionModal, setShowDescriptionModal] = React.useState(false);
  const [selectedTransaction, setSelectedTransaction] = React.useState<ITransaction | undefined>(undefined);

  const handleOpenProofOfPaymentModal = (transaction: ITransaction) => {
    setSelectedTransaction(transaction);
    setShowProofOfPaymentModal(true);
  };

  const handleCloseProofOfPaymentModal = () => {
    setSelectedTransaction(undefined);
    setShowProofOfPaymentModal(false);
  };

  const handleOpenDescriptionModal = (transaction: ITransaction) => {
    setSelectedTransaction(transaction);
    setShowDescriptionModal(true);
  };

  const handleCloseDescriptionModal = () => {
    setShowDescriptionModal(false);
    setSelectedTransaction(undefined);
  };

  const checkIfDebit = (transaction: ITransaction) => transaction.balanceType === EBalanceType.DEBIT;

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#F7F6F6' }}>
              <TableCell>
                <Typography variant="body1" fontWeight={500} color="#434241" align="center">
                  Data
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body1" fontWeight={500} color="#434241" align="center">
                  Transação
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body1" fontWeight={500} color="#434241">
                  Contraparte
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body1" fontWeight={500} color="#434241" align="right">
                  Valor
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body1" fontWeight={500} color="#434241" align="right">
                  Saldo Após
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="body1" fontWeight={500} color="#434241">
                  Descrição
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body1" fontWeight={500} color="#434241" align="center">
                  Comprovante
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {transactions.map((transaction) => (
              <TableRow key={transaction.id} sx={{ borderBottom: '1px solid #E9E8E7' }} hover>
                <TableCell>
                  <Typography variant="body2" align="center">
                    {formatDate(transaction.createdAt)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" align="center">
                    {TABLE_TRANSACTION_TYPE_TRANSLATIONS[transaction.type] || TransactionTypeTranslations[transaction.type]}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{transaction.counterpartyName || '-'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" align="right" whiteSpace="nowrap">
                    {showValues ? (
                      <>
                        {checkIfDebit(transaction) ? '-' : ''}
                        {formatNumberToBRL(transaction.value)}
                      </>
                    ) : (
                      '••••••'
                    )}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" align="right" whiteSpace="nowrap">
                    {(() => {
                      if (!showValues) return '••••••';
                      return transaction.resultingBalance ? formatNumberToBRL(transaction.resultingBalance) : '-';
                    })()}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleOpenDescriptionModal(transaction)}>
                    <Visibility fontSize="small" />
                  </IconButton>
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleOpenProofOfPaymentModal(transaction)}>
                    <Download fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}

            {!loading && transactions.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Nenhuma transação encontrada para o período selecionado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!loading && hasMore && transactions.length > 0 && <div ref={observerTarget} style={{ height: '20px' }} />}

      <ProofOfPaymentModal open={showProofOfPaymentModal} transaction={selectedTransaction} onClose={handleCloseProofOfPaymentModal} />

      {showDescriptionModal && (
        <DescriptionModal
          open={showDescriptionModal}
          onClose={handleCloseDescriptionModal}
          description={selectedTransaction?.description || ''}
        />
      )}
    </>
  );
};
